import { Warning } from "@mui/icons-material";
import { Divider, IconButton, Menu, MenuItem } from "@mui/material";
import { useAuth } from "@ssf/auth-components";
import { queryClient, useLocalStorage } from "@ssf/components";
import { allowedOrdersMemberIds } from "@ssf/memdb-api";
import { DotsVertical } from "mdi-material-ui";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useMatch } from "react-router";
import type { UseQueryClub } from "../lib/index.js";
import { generatePath, routes } from "../lib/index.js";
import { ClubComments, useIsServiceAvailable } from "./index.js";

export const HeaderMenu = () => {
  const { t } = useTranslation("HeaderMenu");
  const clubId = useMatch(`${routes.club}/*`)?.params.clubId;
  const [showComments, setShowComments] = useLocalStorage(
    "showComments",
    false,
  );
  const [anchorEl, setAnchorEl] = useState<HTMLElement>();
  const location = useLocation();
  const { isFedAdmin, user } = useAuth();

  const { isAvailable: isServiceAvailable } =
    useIsServiceAvailable("signMembership");

  // biome-ignore lint/correctness/useExhaustiveDependencies: Observer
  useEffect(() => {
    setAnchorEl(undefined);
  }, [location]);

  if (!isFedAdmin) {
    return null;
  }

  return (
    <>
      <IconButton onClick={({ currentTarget }) => setAnchorEl(currentTarget)}>
        <DotsVertical />
      </IconButton>
      <Menu
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(undefined)}
      >
        <MenuItem component={Link} to={generatePath("clubs", {})}>
          {t("clubs")}
        </MenuItem>
        <MenuItem component={Link} to={generatePath("districts", {})}>
          {t("districts")}
        </MenuItem>
        <MenuItem component={Link} to={generatePath("signatures", {})}>
          {t("signatures")}
        </MenuItem>
        {allowedOrdersMemberIds.includes(Number(user?.profile.sub)) && (
          <MenuItem component={Link} to={generatePath("orders", {})}>
            {t("orders")}
          </MenuItem>
        )}
        <MenuItem
          component={Link}
          to={generatePath("availabilitySettings", {})}
        >
          {t("settings")}
          {!isServiceAvailable ? (
            <Warning color="warning" fontSize="small" sx={{ ml: 1 }} />
          ) : null}
        </MenuItem>
        {clubId && (
          <ClubMenu clubId={clubId}>
            <MenuItem
              onClick={() => {
                setShowComments(!showComments);
                setAnchorEl(undefined);
              }}
            >
              {t(showComments ? "hideComments" : "showComments")}
            </MenuItem>
          </ClubMenu>
        )}
      </Menu>
      {clubId && showComments && (
        <ClubComments
          clubId={Number(clubId)}
          onClose={() => setShowComments(false)}
        />
      )}
    </>
  );
};

const ClubMenu = ({
  clubId,
  children,
}: {
  clubId: number | string;
  children?: ReactNode;
}) => {
  const { t } = useTranslation("HeaderMenu");
  const club = queryClient.getQueryData<UseQueryClub>([
    "useQueryClub",
    +clubId,
  ]);

  if (!club) {
    return null;
  }

  return (
    <>
      <Divider />
      <MenuItem
        disabled
        sx={[
          ({ typography }) => typography.caption,
          {
            textTransform: "uppercase",
            py: 0,
          },
        ]}
      >
        {club.name}
      </MenuItem>
      <MenuItem
        component={Link}
        to={generatePath("clubSignatures", { clubId: club.id.toString() })}
      >
        {t("clubSignatures")}
      </MenuItem>
      <MenuItem
        component={Link}
        to={generatePath("moveMembers", { clubId: club.id.toString() })}
      >
        {t("moveMembers")}
      </MenuItem>
      {children}
    </>
  );
};
